/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useParams } from "@packages/framework";
import type {
  BibleBookId,
  BibleDeeplinks,
  BibleManifest,
  BibleSearchableJson,
  Notation,
} from "@packages/sdk";
import {
  useLocalStorageState,
  useRequestBibleBookSaves,
  useSession,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  HideFromKids,
  RecommendationDeeplink,
  spacing,
  useModal,
} from "@packages/ui";
import type { MouseEventHandler, ReactNode } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useBibleHTML } from "../../../lib";
import { getBibleVerseId } from "../../../lib/utils/getBibleVerseId";
import { BibleActions } from "./BibleActions";
import { BibleBookmarkButton } from "./BibleBookmarkButton";
import { BibleChapterHeader } from "./BibleChapterHeader";
import { BibleNoteModal } from "./BibleNoteModal";
import { BibleNotes } from "./BibleNotes";
import { HighlightColors, useBibleSelection } from "./select.utilities";

const bodyStyle: Interpolation = {
  margin: "auto",
  maxWidth: spacing.maxReaderWidth,
  padding: "40px 15px 150px",
  "& *": {
    transitionProperty: "background, color",
    transitionDuration: "500ms",
    transitionTimingFunction: "ease-out",
  },
};
const bodyMiddleStyle: Interpolation = {
  padding: "0 15px 0",
};
const bodyBottomStyle: Interpolation = {
  padding: "0 15px 150px",
};
const topDeeplinkStyle: Interpolation = {
  padding: `0 15px ${spacing.l}`,
  ".web-app-bible & a": {
    color: constantSemanticColors.onColorHighest,
  },
};
const bottomDeeplinkStyle: Interpolation = {
  padding: `${spacing.l} 15px 0`,
  ".web-app-bible & a": {
    color: constantSemanticColors.onColorHighest,
  },
};
const navBarStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.background,
  display: "flex",
  justifyContent: "space-between",
  marginBlock: theme.spacing.none,
  marginLeft: theme.spacing.none,
  // this matches the paragraph padding to prevent it interacting with the Safari scrollbar,
  // which does not allow/ignores the z-index property on ::-webkit-scrollbar(-thumb)
  marginRight: "15px",
  maxWidth: theme.spacing.maxReaderWidth,
  paddingBlock: theme.spacing.xs,
  position: "sticky",
  top: `calc(${theme.spacing.xxxl} + 56px)`,
  transition: "top 200ms ease-in-out",
  zIndex: theme.zIndices[1],
  [cssMediaQueryMinWidthTablet]: {
    marginInline: "auto",
  },
});

type BibleChapterProps = {
  html: string;
  deeplinks: BibleDeeplinks;
  book: BibleBookId;
  chapter: string | number;
  title: string;
  manifest: BibleManifest;
  translationId: number;
  searchableJson: BibleSearchableJson;
};

export type BibleChapterRef = {
  clearSelection: () => void;
};

export const BibleChapter = forwardRef<BibleChapterRef, BibleChapterProps>(
  (
    {
      html,
      deeplinks,
      book,
      chapter,
      title,
      manifest,
      translationId,
      searchableJson,
    },
    forwardedRef,
  ) => {
    const { locale, translation } = useParams();
    const { status } = useSession();
    const [body, copyright, specialTitle] = useBibleHTML(html);
    const ref = useRef<HTMLDivElement | null>(null);
    const titleRef = useRef<HTMLButtonElement | null>(null);
    const [displayedNote, setDisplayedNote] = useState<ReactNode | null>(null);
    const { data: savesData } = useRequestBibleBookSaves({
      translationId,
      bookId: book,
    });
    const notations = useMemo(() => {
      if (!savesData) return [];
      return savesData.saves.filter(
        (s) =>
          s.type === "notation" && s.chapter === parseInt(chapter.toString()),
      ) as Notation[];
    }, [savesData, chapter]);
    const existingNote = useModal(BibleNoteModal.View);
    const { track } = useAnalytics();

    const [, setLastPage] = useLocalStorageState({
      key: "biblePage",
    });

    const manifestBook = useMemo(
      () => manifest.books.find((bk) => bk.id === book),
      [manifest, book],
    );

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([intersection]) => {
          if (intersection.isIntersecting) {
            const previousChapter = location.href?.split("/")?.slice(-1)?.[0];
            // Use History.prototype directly to bypass Next.js's instance-level
            // patch of history.replaceState, which triggers a soft navigation,
            // re-render, and scroll jump that creates a feedback cycle.
            Object.getPrototypeOf(window.history).replaceState.call(
              window.history,
              window.history.state,
              "",
              `${locale === "en" ? "" : `/${locale}`}/bible/${translation}/${book}/${chapter}`,
            );
            track({
              event: HallowAnalyticsEvent.ChangedBibleChapter,
              properties: {
                bible_language: manifest.translation_language,
                bible_translation: translation,
                bible_book_id: book,
                navigation_source: "scroll",
                screen_name: "bible",
                starting_book: book,
                starting_chapter: isNaN(parseInt(previousChapter))
                  ? previousChapter
                  : parseInt(previousChapter),
                starting_translation: translation,
                new_chapter: chapter,
              },
            });
            setLastPage(`${book}/${chapter}`);
          }
        },
        {
          root: null,
          rootMargin: "10% 0px -90% 0px",
          threshold: 0,
        },
      );
      if (titleRef.current) {
        observer.observe(titleRef.current);
        return () => {
          if (titleRef.current) observer.unobserve(titleRef.current);
        };
      }
    }, []);

    const {
      selection,
      textSelection,
      selectionTitle,
      handleClickForSelection,
      clearSelection,
      handleDrop,
    } = useBibleSelection({
      ref,
      book,
      manifestBook,
      searchableJson,
      manifest,
    });

    useImperativeHandle(forwardedRef, () => ({
      clearSelection,
    }));

    const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        e.stopPropagation();

        const source = e.target as HTMLElement;
        //#region Notes
        const link = source.closest("a");
        if (link?.href) {
          e.preventDefault();
          e.stopPropagation();
          const hash = new URL(link.href, location.href).hash;
          const linkBackEl = ref.current.querySelector(hash);
          const noteEl = linkBackEl.closest(".footnote, .endnote");
          setDisplayedNote(
            <div className="web-app-bible">
              <div
                className="with-notes notes visible"
                css={{
                  marginBottom: "0 !important",
                }}
                dangerouslySetInnerHTML={{ __html: noteEl.outerHTML }}
              />
            </div>,
          );
          return;
        }
        //#endregion

        //#region User Notations
        const userNote = source.closest(".noteIcon");
        if (userNote) {
          try {
            const noteData: Notation = JSON.parse(
              (userNote as HTMLElement).dataset.note,
            );
            existingNote.show({
              book: noteData.book_id as BibleBookId,
              chapter: noteData.chapter,
              color: noteData.color as HighlightColors,
              end: noteData.verse_end,
              notationId: noteData.id,
              note: noteData.note.toString(),
              start: noteData.verse_start,
              text: noteData.selected_text,
              title: noteData.title,
              translationId: noteData.translation_id,
              translationName: noteData.translation_name,
            });
            return;
          } catch (e) {
            console.error(e);
          }
        }
        //#endregion

        //#region Text selection
        let verseWrapper = source.closest(".verseWrapper");
        if (!verseWrapper) {
          const targetY = e.clientY;
          const verses = source.querySelectorAll(".verseWrapper");

          for (let v = 0; v < verses.length; v++) {
            const box = verses[v].getBoundingClientRect();
            if (box.top < targetY && box.bottom > targetY) {
              verseWrapper = verses[v];
              break;
            }
          }
        }
        if (verseWrapper) {
          handleClickForSelection(verseWrapper as HTMLElement);
        }
        //#endregion
      },
      [handleClickForSelection],
    );

    useLayoutEffect(() => {
      // this intentionally has no dependency array because we want to run it anytime this component renders
      // this is idempotent because dangerouslySetInnerHTML is dynamic, not static -- it will rerender even if
      // the body text does not change on component rerenders, and when that happens, all of our inserts and
      // modifications are lost
      if (ref.current) {
        for (const notation of notations) {
          const arr = new Array(notation.verse_end - notation.verse_start + 1)
            .fill(null)
            .map((_, idx) => notation.verse_start + idx)
            .map(
              (v) =>
                `.verse-${getBibleVerseId({ chapter: parseInt(chapter.toString()), verse: v, book, manifest })}`,
            );
          const verses = Array.from(
            ref.current.querySelectorAll(arr.join(",")),
          ).filter(
            (v) =>
              !v.parentElement?.classList?.contains("enref") &&
              !v.parentElement?.classList?.contains("fnref"),
          );
          if (notation.color && verses?.length) {
            verses.forEach((v) => {
              for (const c of Object.values(HighlightColors)) {
                // only apply the most recent highlight color, determined by order of notations from the backend
                v.classList.remove(`highlight-${c}`);
              }
              v.classList.add("user-highlight", `highlight-${notation.color}`);
            });
          }
          if (notation.note && verses?.length) {
            const lastVerse = verses[verses.length - 1];
            // remove all previous note icons that would appear at the end of this verse
            const existingNote = lastVerse.querySelector(".noteIcon");
            if (existingNote)
              existingNote.parentElement?.removeChild(existingNote);
            // add the note icon
            const noteIcon = document.createElement("button");
            noteIcon.type = "button";
            noteIcon.classList.add("noteIcon");
            noteIcon.dataset.note = JSON.stringify(notation);
            lastVerse.insertAdjacentElement("beforeend", noteIcon);
          }
        }
      }
    });

    return (
      <section
        id={`_${book}_${chapter}`}
        ref={ref}
        className={translationId === 2 ? "with-notes" : ""}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={clearSelection}
        css={{
          userSelect: "none",
          scrollSnapAlign: "start",
          scrollMarginTop: `calc(${spacing.xxxl} + 56px)`,
        }}
      >
        <BibleNotes
          note={displayedNote}
          searchableJson={searchableJson}
          manifest={manifest}
        />
        <BibleActions
          title={selectionTitle}
          text={textSelection}
          {...(selection
            ? selection
            : { chapter: null, start: null, end: null })}
          onClose={() => clearSelection()}
        />
        <div css={navBarStyle}>
          <BibleChapterHeader book={book} manifest={manifest} ref={titleRef}>
            {specialTitle ?? title}
          </BibleChapterHeader>
          {status === "authenticated" && (
            <BibleBookmarkButton
              book={book}
              chapter={chapter}
              title={title}
              translationId={translationId}
            />
          )}
        </div>
        {deeplinks?.top_content_deeplink ? (
          <HideFromKids>
            <div css={[bodyStyle, topDeeplinkStyle]}>
              <RecommendationDeeplink {...deeplinks.top_content_deeplink} />
            </div>
          </HideFromKids>
        ) : null}
        <div
          css={[bodyStyle, bodyMiddleStyle]}
          onClick={handleClick}
          dangerouslySetInnerHTML={{ __html: body }}
        />
        {deeplinks?.bottom_content_deeplink ? (
          <HideFromKids>
            <div css={[bodyStyle, bottomDeeplinkStyle]}>
              <RecommendationDeeplink {...deeplinks.bottom_content_deeplink} />
            </div>
          </HideFromKids>
        ) : null}
        <div
          css={[bodyStyle, bodyBottomStyle]}
          dangerouslySetInnerHTML={{ __html: copyright }}
          onClick={(e) => {
            const clicked: HTMLElement = e.target as HTMLElement;
            if (clicked?.classList?.contains("copyright")) {
              e.stopPropagation();
              clicked.classList.toggle("expanded");
            }
          }}
        />
      </section>
    );
  },
);

BibleChapter.displayName = "BibleChapter";
